import { useState, useEffect, useCallback } from 'react';
import api from 'services/api';
import useEscapeClose from 'hooks/useEscapeClose';

const CLASSES = [
  { val: 2, label: 'Classe 2 — Immobilisations' },
  { val: 3, label: 'Classe 3 — Stocks' },
  { val: 4, label: 'Classe 4 — Tiers' },
  { val: 5, label: 'Classe 5 — Trésorerie' },
  { val: 6, label: 'Classe 6 — Charges' },
  { val: 7, label: 'Classe 7 — Produits' },
];

export default function PlanComptablePage() {
  const [comptes, setComptes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterClasse, setFilterClasse] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ numero: '', libelle: '', classe: 6, type: 'debit' });

  useEscapeClose(showForm, () => setShowForm(false));

  const load = useCallback(async () => {
    try { const r = await api.get('/syscohada/plan-comptable'); setComptes(r.data); } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try { await api.post('/syscohada/plan-comptable', form); setShowForm(false); load(); } catch {}
  };

  const filtered = comptes.filter(c => !filterClasse || c.classe === Number(filterClasse));
  const grouped = {};
  filtered.forEach(c => {
    const key = c.classe;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(c);
  });

  return (
    <div>
      <div className="stats-grid stats-grid--3">
        <div className="stat-card"><div className="stat-card-header"><span className="stat-card-label">Comptes actifs</span></div><div className="stat-card-value">{comptes.filter(c => c.actif).length}</div></div>
        <div className="stat-card"><div className="stat-card-header"><span className="stat-card-label">Classes</span></div><div className="stat-card-value">{new Set(comptes.map(c => c.classe)).size}</div></div>
        <div className="stat-card"><div className="stat-card-header"><span className="stat-card-label">Norme</span></div><div className="stat-card-value" style={{ fontSize: 16 }}>SYSCOHADA</div></div>
      </div>

      <div className="content-card">
        <div className="gss-toolbar">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div className="content-card-title">Plan comptable SYSCOHADA</div>
            <select className="gss-input" value={filterClasse} onChange={e => setFilterClasse(e.target.value)} style={{ width: 200 }}>
              <option value="">Toutes classes</option>
              {CLASSES.map(c => <option key={c.val} value={c.val}>{c.label}</option>)}
            </select>
          </div>
          <button className="gss-btn gss-btn-primary" onClick={() => setShowForm(true)}>+ Nouveau compte</button>
        </div>

        {loading ? <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p> : (
          Object.entries(grouped).sort(([a], [b]) => a - b).map(([classe, items]) => (
            <div key={classe} style={{ marginBottom: 16 }}>
              <div style={{ padding: '8px 16px', fontSize: 12, fontWeight: 700, color: '#8B1A1A', textTransform: 'uppercase', background: '#f9f9f9', borderBottom: '1px solid #eee' }}>
                {CLASSES.find(c => c.val === Number(classe))?.label || `Classe ${classe}`}
              </div>
              <div className="gss-table-wrap">
                <table className="gss-table">
                  <thead><tr><th style={{ width: 80 }}>N°</th><th>Libellé</th><th style={{ width: 80 }}>Type</th><th style={{ width: 80 }}>Statut</th></tr></thead>
                  <tbody>
                    {items.map(c => (
                      <tr key={c.id} style={{ opacity: c.actif ? 1 : 0.4 }}>
                        <td style={{ fontWeight: 700, fontFamily: 'monospace' }}>{c.numero}</td>
                        <td>{c.libelle}</td>
                        <td><span className="gss-badge" style={{ background: c.type === 'debit' ? '#1565c020' : '#2e7d3220', color: c.type === 'debit' ? '#1565c0' : '#2e7d32' }}>{c.type === 'debit' ? 'Débit' : 'Crédit'}</span></td>
                        <td>{c.actif ? <span style={{ color: '#2e7d32' }}>Actif</span> : <span style={{ color: '#888' }}>Inactif</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>

      {showForm && (
        <div className="gss-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="gss-modal" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header"><h2>Nouveau compte</h2><button className="gss-modal-close" onClick={() => setShowForm(false)}>&times;</button></div>
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '1.5rem' }}>
                <div className="gss-form-grid">
                  <div className="gss-field"><label>N° compte *</label><input className="gss-input" required value={form.numero} onChange={e => setForm({ ...form, numero: e.target.value })} placeholder="Ex: 661" /></div>
                  <div className="gss-field"><label>Libellé *</label><input className="gss-input" required value={form.libelle} onChange={e => setForm({ ...form, libelle: e.target.value })} /></div>
                  <div className="gss-field"><label>Classe</label>
                    <select className="gss-input" value={form.classe} onChange={e => setForm({ ...form, classe: Number(e.target.value) })}>
                      {CLASSES.map(c => <option key={c.val} value={c.val}>{c.label}</option>)}
                    </select>
                  </div>
                  <div className="gss-field"><label>Type</label>
                    <select className="gss-input" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                      <option value="debit">Débit</option><option value="credit">Crédit</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="gss-modal-actions"><button type="button" className="gss-btn-cancel" onClick={() => setShowForm(false)}>Annuler</button><button type="submit" className="gss-btn-deploy">Créer</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
